import {
  CalculatorOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { OptionList } from '@atoms';
import {
  ACCESS_PROFILE_ID_BY_PLAN_TYPE,
  ACQUISITION_TYPE_CODES,
} from '@features/offers/constants';
import useFormOffer from '@features/offers/hooks/useFormOffer';
import { OfferFormData } from '@features/offers/interfaces/offersInterface';
import { generateInstallments } from '@features/offers/utils/helpers';
import {
  Controller,
  DatePickerController,
  RadioController,
  SelectController,
  SwitchController,
  TextAreaController,
} from '@organisms';
import { Button, Col, Flex, Row, Tooltip, Typography } from 'antd';
import { FC, useMemo, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { CurrencyController } from 'src/app/framework/organisms/Controllers/CurrencyController.organism';
import { SwitchGroupController } from 'src/app/framework/organisms/Controllers/SwitchGroupController.organism';
import { parseCurrency } from 'src/utils/currency.util';

import { PaymentOptions } from './PaymentOptions';
import { PriceCalculatorModal } from './PriceCalculatorModal';
import { RetryDiscountTable } from './RetryDiscountTable';

type CalculablePriceField = 'fullPrice' | 'displayPrice';

interface FormSensitiveDataProps {
  control: Control<OfferFormData>;
  product: ReturnType<typeof useFormOffer>['product'];
  formType: ReturnType<typeof useFormOffer>['formType'];
  isEditOrView: boolean;
  acquisition: ReturnType<typeof useFormOffer>['acquisition'];
  hasPromotionalOffer: boolean;
  isOfferStandard: boolean;
  hasRetryDiscounts: boolean;
  acquisitionTypeIdsByCodes: ReturnType<
    typeof useFormOffer
  >['acquisitionTypeIdsByCodes'];
  acquisitionTypeOptions: OptionList;
  seasonalityOptions: OptionList;
  typeOptions: OptionList;
  tasFieldDisabled: ReturnType<typeof useFormOffer>['tasFieldDisabled'];
  handleRetryDiscounts: ReturnType<
    typeof useFormOffer
  >['handleUpdateRetryDiscountValue'];
  handleUpdateFullPrice: ReturnType<
    typeof useFormOffer
  >['handleUpdateFullPrice'];
  handleApplyCalculatedPrice: ReturnType<
    typeof useFormOffer
  >['handleApplyCalculatedPrice'];
  upsellFlowOptions: ReturnType<typeof useFormOffer>['upsellFlowOptions'];
}

export const FormSensitiveData: FC<FormSensitiveDataProps> = ({
  control,
  product,
  isEditOrView,
  formType,
  acquisition,
  isOfferStandard,
  hasRetryDiscounts,
  handleApplyCalculatedPrice,
  handleUpdateFullPrice,
  handleRetryDiscounts,
  hasPromotionalOffer,
  acquisitionTypeIdsByCodes,
  acquisitionTypeOptions,
  seasonalityOptions,
  typeOptions,
  tasFieldDisabled,
  upsellFlowOptions,
}: FormSensitiveDataProps) => {
  const { Text } = Typography;
  const isRequiredUpsellFlow = formType === 'edit' && isOfferStandard;
  const [calculatorField, setCalculatorField] =
    useState<CalculablePriceField | null>(null);
  const [fullPrice, displayPrice] = useWatch({
    control,
    name: ['sensitiveData.fullPrice', 'sensitiveData.displayPrice'],
  });

  const priceByField: Record<CalculablePriceField, string> = {
    fullPrice,
    displayPrice,
  };
  const fieldLabelByName: Record<CalculablePriceField, string> = {
    fullPrice: 'Valor da Oferta',
    displayPrice: 'Display price',
  };
  const hasValidPrice = (value: string) => (parseCurrency(value) ?? 0) > 0;
  const canCalculateFullPrice = !isEditOrView && hasValidPrice(fullPrice);
  const canCalculateDisplayPrice =
    formType !== 'view' && hasValidPrice(displayPrice);
  const installmentsOptions = useMemo(
    () => generateInstallments(fullPrice),
    [fullPrice]
  );

  const calculatorButton = (field: CalculablePriceField) => (
    <Tooltip title={`Calcular ${fieldLabelByName[field]}`}>
      <Button
        aria-label={`Abrir calculadora de ${fieldLabelByName[field]}`}
        icon={<CalculatorOutlined />}
        onMouseDown={event => event.preventDefault()}
        onClick={event => {
          event.stopPropagation();
          setCalculatorField(field);
        }}
        size='small'
        type='text'
      />
    </Tooltip>
  );

  const switchOptions = [
    { value: '', label: 'Individual' },
    { value: `${ACCESS_PROFILE_ID_BY_PLAN_TYPE.duo}`, label: 'Duo' },
    { value: `${ACCESS_PROFILE_ID_BY_PLAN_TYPE.family}`, label: 'Familia' },
  ];

  return (
    <Flex vertical style={{ gap: '24px' }}>
      <Row
        style={{
          display: 'flex',
          width: '100%',
          gap: '24px',
        }}
      >
        <Col style={{ flex: 1 }}>
          <SelectController
            label={'Tipo de venda'}
            required
            options={typeOptions}
            control={control}
            name={'sensitiveData.type'}
            disabled={isEditOrView || tasFieldDisabled.type}
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <SelectController
            label={'Aquisição'}
            required
            options={acquisitionTypeOptions}
            control={control}
            name={'sensitiveData.acquisition'}
            disabled={isEditOrView || tasFieldDisabled.acquisition}
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <SelectController
            label={'Sazonalidade'}
            required
            options={seasonalityOptions}
            control={control}
            name={'sensitiveData.seasonality'}
            disabled={
              isEditOrView ||
              tasFieldDisabled.seasonality ||
              product?.subscriptionType === 'monthly'
            }
          />
        </Col>
      </Row>
      <Row
        style={{
          display: 'flex',
          width: '100%',
          gap: '24px',
        }}
      >
        <Col style={{ flex: 1 }}>
          <CurrencyController
            disabled={isEditOrView}
            control={control}
            name={`sensitiveData.fullPrice`}
            label='Valor da Oferta'
            onValueChange={handleUpdateFullPrice}
            suffix={
              canCalculateFullPrice ? calculatorButton('fullPrice') : undefined
            }
            required
            reserveErrorSpace
            tooltip={{
              title: () => (
                <Typography.Text>
                  O valor da oferta não pode ser alterado.
                </Typography.Text>
              ),
            }}
          />
        </Col>
        <Col style={{ flex: 0.5, marginTop: -20 }}>
          <SwitchController
            control={control}
            name='sensitiveData.hasRetryDiscounts'
            label='Múltiplos valores'
            tooltip={{
              title: () => (
                <Typography.Text>
                  Permite a configuração de múltiplos valores para a oferta.
                </Typography.Text>
              ),
            }}
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <SelectController
            label={'Parcelamento'}
            required
            options={installmentsOptions}
            defaultValue={
              installmentsOptions[installmentsOptions.length - 1].value
            }
            control={control}
            name={'sensitiveData.installments'}
            disabled={isEditOrView || product?.subscriptionType === 'monthly'}
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <CurrencyController
            control={control}
            name={`sensitiveData.displayPrice`}
            autoComplete='off'
            label='Display price'
            reserveErrorSpace
            suffix={
              canCalculateDisplayPrice
                ? calculatorButton('displayPrice')
                : undefined
            }
          />
        </Col>
      </Row>
      {calculatorField && (
        <PriceCalculatorModal
          open
          fieldLabel={fieldLabelByName[calculatorField]}
          baseValue={priceByField[calculatorField]}
          onApply={value => {
            handleApplyCalculatedPrice(calculatorField, value);
            setCalculatorField(null);
          }}
          onClose={() => setCalculatorField(null)}
        />
      )}
      <Row style={{ width: '100%' }}>
        {hasRetryDiscounts && (
          <Row style={{ width: '100%' }}>
            <Typography.Text
              style={{ fontSize: 14, lineHeight: '22px', marginBlockEnd: 8 }}
            >
              Lista de múltiplos valores
            </Typography.Text>
            <RetryDiscountTable
              control={control}
              formType={formType}
              product={product}
              handleRetryDiscounts={handleRetryDiscounts}
            />
          </Row>
        )}
      </Row>
      <Row
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          gap: '24px',
        }}
      >
        <Col style={{ flex: 1 }}>
          <DatePickerController
            showTime={true}
            label='Selecione a data de ativação da oferta'
            control={control}
            name={'sensitiveData.startDate'}
            format='DD/MM/YYYY HH:mm:ss'
            required
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <DatePickerController
            showTime={true}
            label='Selecione a data de expiração da oferta'
            control={control}
            name={'sensitiveData.endDate'}
            format='DD/MM/YYYY HH:mm:ss'
            disabled={isEditOrView && isOfferStandard}
            required={!isOfferStandard}
          />
        </Col>
        <Col style={{ flex: 1 }}>
          <Controller
            control={control}
            showCount
            name={`sensitiveData.purchaseBtnLabel`}
            placeholder='Compre Agora'
            autoComplete='off'
            label='Descritivo do botão'
            maxLength={54}
          />
        </Col>
      </Row>
      <Row style={{ width: '100%' }}>
        <SwitchController
          control={control}
          defaultValue={false}
          name='sensitiveData.offerStandard'
          sideLabel
          leftLabel='Esta oferta substitui a oferta padrão?'
          required
          tooltip={{
            title: () => (
              <Typography.Text>
                Somente um usuário master pode ativar essa opção.
              </Typography.Text>
            ),
          }}
        />
      </Row>
      <Row style={{ width: '100%', gap: '56px' }}>
        <Flex>
          <SwitchGroupController
            control={control}
            name='sensitiveData.plan'
            label='Planos'
            options={switchOptions}
          />
        </Flex>
        <Flex style={{ gap: '56px' }}>
          <Col>
            <Text
              style={{
                width: '100%',
                lineHeight: '28px',
                paddingInlineEnd: '16px',
              }}
            >
              É e-mail promocional?
            </Text>
            <RadioController
              control={control}
              name='sensitiveData.isMailPromotional'
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
            />
          </Col>
          <Col>
            <Text
              style={{
                width: '100%',
                lineHeight: '28px',
                paddingInlineEnd: '16px',
              }}
            >
              Desativar Renovação Automática?
            </Text>
            <RadioController
              control={control}
              name='sensitiveData.renewalDisabled'
              disabled={
                acquisition ===
                  `${acquisitionTypeIdsByCodes?.[ACQUISITION_TYPE_CODES.rent]}` ||
                acquisition ===
                  `${acquisitionTypeIdsByCodes?.[ACQUISITION_TYPE_CODES.purchase]}` ||
                hasPromotionalOffer
              }
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
            />
          </Col>
          <Col>
            <Text
              style={{
                width: '100%',
                lineHeight: '28px',
                paddingInlineEnd: '16px',
              }}
            >
              Somente pagamento full (valor integral)?
            </Text>
            <RadioController
              control={control}
              name='sensitiveData.onlyFull'
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
              disabled={
                formType === 'view' ||
                product?.subscriptionType === 'monthly' ||
                product?.isDirectFallback
              }
            />
          </Col>
        </Flex>
      </Row>
      <Row style={{ display: 'flex', padding: '10px 0', gap: '24px' }}>
        <Col style={{ flex: '1 1 0', minWidth: 0 }}>
          <SelectController
            control={control}
            name='sensitiveData.flowId'
            label='Funil de upsell'
            placeholder='Busque por ID ou nome'
            options={upsellFlowOptions.options}
            loading={upsellFlowOptions.isLoading}
            suffixIcon={
              upsellFlowOptions.isLoading ? (
                <LoadingOutlined spin />
              ) : (
                <SearchOutlined />
              )
            }
            showSearch
            allowClear
            filterOption={false}
            onSearch={upsellFlowOptions.setSearch}
            notFoundContent={null}
            disabled={formType === 'view'}
            required={isRequiredUpsellFlow}
            style={{ width: '100%' }}
          />
        </Col>
        <Col flex={1}>
          <Controller
            control={control}
            name='sensitiveData.renewalOfferId'
            placeholder='Insira o ID da oferta'
            label='Renovação em outra oferta?'
            type='number'
          />
        </Col>
        <Col flex={1}>
          <Controller
            control={control}
            name='sensitiveData.profileId'
            placeholder='Insira o ID do profile de liberação'
            label='Profile de liberação'
            type='number'
          />
        </Col>
        <Col flex={1}>
          <Controller
            control={control}
            name='sensitiveData.warrantyDays'
            placeholder='Insira a quantidade de dias de garantia'
            label='Garantia'
            type='number'
          />
        </Col>
      </Row>
      <PaymentOptions control={control} />
      <Row style={{ display: 'flex' }}>
        <Col style={{ width: '100%' }}>
          <TextAreaController
            control={control}
            name='sensitiveData.disclaimer'
            placeholder='Área de texto'
            autoComplete='off'
            label='Disclaimer - Indique e Ganhe'
            isMaxLength={true}
            row={4}
            maxLength={600}
          />
        </Col>
      </Row>
      <Row style={{ width: '100%', justifyContent: 'space-between' }}>
        <Col style={{ minWidth: '49%', marginInlineEnd: '12px' }}>
          <Controller
            control={control}
            name={`sensitiveData.termsRef`}
            defaultValue={'https://www.queimadiaria.com/termos-de-uso/'}
            placeholder=''
            autoComplete='off'
            label='Link para termos de responsabilidade'
            required
          />
        </Col>
        <Col style={{ marginInlineStart: '12px', minWidth: '49%' }}>
          <Controller
            control={control}
            name={`sensitiveData.privacyPolicyRef`}
            defaultValue={
              'https://www.queimadiaria.com/politica-de-privacidade/'
            }
            placeholder=''
            autoComplete='off'
            label='Link para politica de privacidade'
            required
          />
        </Col>
      </Row>
    </Flex>
  );
};
